'use client';
import React, { useEffect, useRef } from 'react';
import { Button, Div, Text, TextField } from '@pezeshk-book/ui-kit';
import { CountDown } from '@pezeshk-book/modules';
import { AuthActions } from '@store/auth/auth-actions';
import { useDebouncedCallback } from 'use-debounce';
import validMobileNumber from '@utils/helpers/phone-validation';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@store/root-reducer';
import { useParams, useSearchParams } from 'next/navigation';
import dictionaryParser from '@utils/helpers/dictionary-parser';
import routes from '@routes';
import parse from 'html-react-parser';
import getParseRoute from '@utils/helpers/parse-route';
import { LocaleTypes } from '@dictionaries-types';

const InputFields = ({ isMobile = false, translate }: {isMobile?: boolean, translate: any}) => {
  const dispatch = useDispatch();
  const { confirmLoading, validationCodeLifetime, sendVerificationLoading, isNumberValid, verifyCode, countDownOver, countDownReset, isAlreadyGetTheCodeFlag, isRetryingToGetNewCode, isLoggedIn } = useSelector((state: RootState) => state.auth);
  const codeRef = useRef<HTMLInputElement>(null);
  const redirect = useSearchParams().get('redirect');
  const { lang } = useParams<{lang: LocaleTypes}>();

  useEffect(() => {
    if ('OTPCredential' in window) {
      const ac = new AbortController();

      navigator.credentials
        .get({
          otp: { transport: ['sms'] },
          signal: ac.signal,
        } as CredentialRequestOptions & { otp: { transport: string[] } })
        .then((otpCredential: any) => {
          dispatch(AuthActions.checkSmsCodeAndLogin({ verifyCode: otpCredential.code }));
          ac.abort();
        })
        .catch(() => {
          ac.abort();
          // console.log(err);
        });
    }
  });

  useEffect(() => {
    if (redirect) {
      const redirectRoute = typeof redirect === 'object' ? redirect[0] : redirect;
      dispatch(AuthActions.setInitialState({ redirectRoute: decodeURIComponent(redirectRoute) }));
    } else if (isLoggedIn) {
      dispatch(AuthActions.setInitialState({ redirectRoute: isMobile ? `/${lang}/mobile` : `/${lang}` }));
    }
  }, [redirect, isLoggedIn]);

  const counterCallbackFunction = () => {
    dispatch(AuthActions.setCountDownOver());
  };

  const mobileValidation = useDebouncedCallback((e) => {
    const value = e.target.value;
    if (value.length >= 10) {
      const validMobile = validMobileNumber(value);
      if (validMobile.isValid) {
        dispatch(AuthActions.setClientMobile({ phoneNumber: `0${validMobile.mobile}` }));
      } else {
        dispatch(AuthActions.setMobileValidation({ isNumberValid: false }));
      }
    } else if (isNumberValid) {
      dispatch(AuthActions.setMobileValidation({ isNumberValid: false }));
    }
  }, 500);

  const clientVerificationCode = ({ type, isRetryingToGetNewCode }: { type: string, isRetryingToGetNewCode: boolean }) => {
    dispatch(AuthActions.sendVerificationCode({ type: type, isRetryingToGetNewCode: isRetryingToGetNewCode }));
  };

  const codeInputHandler = (e: any) => {
    dispatch(AuthActions.setValidationCode({ verifyCode: e?.target?.value }));
  };

  const getCode = () => {
    if (codeRef?.current) {
      codeRef?.current?.focus();
    }
    clientVerificationCode({ type: 'sms', isRetryingToGetNewCode: false });
  };

  return (
    <Div className={'z-10 flex-col items-end pt-16 w-full sm:w-7/12 gap-10'}>
      <TextField
        size={'large'}
        inputMode={'numeric'}
        onChange={mobileValidation}
        placeholder={translate.mobile_placeholder}
        placeholderPosition={'center'}
        className={'w-full md:w-3/4'}
        inputClassName={'!text-center sm:!text-d-lg tracking-[10px] sm:tracking-[20px] placeholder-shown:tracking-normal'}
      />
      <Div className={'flex-col w-full md:w-3/4 gap-2'}>
        <Div>
          <TextField
            value={verifyCode}
            ref={codeRef}
            type={'text'}
            autoComplete={'one-time-code'}
            size={'large'}
            disabled={!isNumberValid}
            inputMode={'numeric'}
            maxLength={4}
            onChange={codeInputHandler}
            placeholder={'____'}
            placeholderPosition={'center'}
            className={'w-full'}
            inputClassName={'!text-center sm:!text-d-lg tracking-[20px]'}
          />
        </Div>
        <Text
          align={'start'}
          color={'grey.500'}
          typography={'sm'}>
          {translate.otp_sms_text}
        </Text>
      </Div>
      <Div className={'md:mt-10 w-full md:w-3/4 flex-col gap-4'}>
        {!countDownOver && !isAlreadyGetTheCodeFlag || ((!countDownOver && isAlreadyGetTheCodeFlag) || isRetryingToGetNewCode) ? (
          <Button
            loading={sendVerificationLoading || confirmLoading}
            size={'large'}
            disabled={!isNumberValid || confirmLoading}
            onClick={getCode}
            className={'min-w-28 sm:min-w-36 md:min-w-48 whitespace-nowrap'}>
            {translate.buttons.get_code}
          </Button>
        ) : (
          <Div className={'min-h-12 md:min-h-14 bg-amber-200 w-full flex-col'}>
            <CountDown
              variant={'contained'}
              parentCallback={counterCallbackFunction}
              sec={validationCodeLifetime}
              reset={countDownReset}
              className={`flex-1`}
            />
          </Div>
        )}
        <Text align={'start'} typography={'xs'}>
          {parse(dictionaryParser({ content: translate.accept_policy, variables: [{ href: getParseRoute({ pathname: isMobile ? `/mobile${routes['route.policy.index']}` : routes['route.policy.index'], locale: lang }) }] }))}
        </Text>
      </Div>
    </Div>
  );
};

export default InputFields;
